import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Calendar } from 'antd';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import { useAuth } from '../../context/AuthContext';
import { useCurrentUserProfile } from '../../hooks/useCurrentUserProfile';
import { useChurchPlanner } from '../../hooks/useChurchPlanner';
import { hasAnyPermission, Permission } from '../../utils/roles';
import {
  PlannerPage,
  PlannerCard,
  PlannerHeading,
  PlannerKicker,
  PlannerSubtle,
  PlannerError,
  plannerColors,
} from './plannerTheme';

const Grid = styled.div`
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  font-size: 0.8rem;
  color: ${plannerColors.muted};
`;

const Input = styled.input`
  border: 1px solid ${plannerColors.border};
  border-radius: 10px;
  background: ${plannerColors.surfaceHover};
  color: ${plannerColors.text};
  padding: 0.52rem 0.62rem;
`;

const Textarea = styled.textarea`
  border: 1px solid ${plannerColors.border};
  border-radius: 10px;
  background: ${plannerColors.surfaceHover};
  color: ${plannerColors.text};
  min-height: 86px;
  resize: vertical;
  padding: 0.52rem 0.62rem;
`;

const Select = styled.select`
  border: 1px solid ${plannerColors.border};
  border-radius: 10px;
  background: ${plannerColors.surfaceHover};
  color: ${plannerColors.text};
  padding: 0.52rem 0.62rem;
`;

const ActionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem;
  margin-top: 0.7rem;
`;

const Button = styled.button<{ $tone?: 'danger' | 'muted' }>`
  border-radius: 10px;
  border: 1px solid
    ${({ $tone }) =>
      $tone === 'danger'
        ? 'rgba(255, 69, 58, 0.35)'
        : $tone === 'muted'
          ? plannerColors.border
          : 'transparent'};
  background: ${({ $tone }) =>
    $tone === 'danger' ? 'rgba(255, 69, 58, 0.12)' : $tone === 'muted' ? plannerColors.surfaceHover : plannerColors.accent};
  color: ${({ $tone }) => ($tone === 'danger' ? plannerColors.danger : $tone === 'muted' ? plannerColors.text : '#1a1a1a')};
  font-size: 0.76rem;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  padding: 0.42rem 0.65rem;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const RangeList = styled.div`
  display: grid;
  gap: 0.65rem;
`;

const RangeCard = styled.div`
  border: 1px solid ${plannerColors.border};
  border-radius: 12px;
  background: ${plannerColors.surfaceHover};
  padding: 0.72rem;
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 0.7rem;
  align-items: center;
  flex-wrap: wrap;
`;

const CalendarFrame = styled.div`
  margin-top: 0.7rem;
  border: 1px solid ${plannerColors.border};
  border-radius: 12px;
  overflow: hidden;

  .ant-picker-calendar {
    background: ${plannerColors.surfaceHover};
  }

  .ant-picker-panel {
    background: ${plannerColors.surfaceHover};
  }

  .ant-picker-content th {
    color: ${plannerColors.muted};
    font-weight: 600;
  }
`;

function toDateInputValue(iso?: string | null): string {
  if (!iso) return '';
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return '';
  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function toInclusiveEndDateValue(endsAt?: string | null): string {
  if (!endsAt) return '';
  const parsed = new Date(endsAt);
  if (Number.isNaN(parsed.getTime())) return '';
  parsed.setDate(parsed.getDate() - 1);
  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function toStartEndIso(startDate: string, endDate: string): { startsAt: string; endsAt: string } | null {
  if (!startDate || !endDate) return null;
  const start = new Date(`${startDate}T00:00:00`);
  const end = new Date(`${endDate}T00:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
  if (end.getTime() < start.getTime()) return null;
  const endExclusive = new Date(end);
  endExclusive.setDate(endExclusive.getDate() + 1);
  return {
    startsAt: start.toISOString(),
    endsAt: endExclusive.toISOString(),
  };
}

function formatRange(startsAt: string, endsAt: string): string {
  const start = new Date(startsAt);
  const endExclusive = new Date(endsAt);
  if (Number.isNaN(start.getTime()) || Number.isNaN(endExclusive.getTime())) {
    return `${startsAt} - ${endsAt}`;
  }
  const end = new Date(endExclusive);
  end.setDate(end.getDate() - 1);
  return `${start.toLocaleDateString()} - ${end.toLocaleDateString()}`;
}

function getPersonDisplayLabel(person: {
  id: string;
  email?: string | null;
  name?: string;
  full_name?: string | null;
}): string {
  return person.name || person.full_name || person.email || person.id;
}

export default function ChurchPlannerCalendarPage() {
  const { user } = useAuth();
  const { data: profile } = useCurrentUserProfile(user?.id);
  const planner = useChurchPlanner(true, user?.id);

  const roles = profile?.roles ?? user?.user_metadata?.roles;
  const canManageOthers = hasAnyPermission(roles, [
    Permission.MANAGE_CHURCH_PLAN_ASSIGNMENTS,
    Permission.MANAGE_CHURCH_PLANNER_ROLES,
  ]);

  const [selectedUserId, setSelectedUserId] = useState<string>(user?.id || '');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [notes, setNotes] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [pickStep, setPickStep] = useState<'start' | 'end'>('start');
  const [calendarValue, setCalendarValue] = useState<Dayjs>(() => dayjs());

  const peopleOptions = planner.people.length ? planner.people : planner.plannerMembers;

  const selectedRows = useMemo(() => {
    const rows = planner.memberUnavailability ?? [];
    if (!selectedUserId) return [];
    return rows
      .filter((row) => row.user_id === selectedUserId)
      .sort((a, b) => new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime());
  }, [planner.memberUnavailability, selectedUserId]);

  const resetForm = () => {
    setEditingId(null);
    setStartDate('');
    setEndDate('');
    setNotes('');
    setPickStep('start');
  };

  const handleEdit = (row: (typeof selectedRows)[number]) => {
    const nextStartDate = toDateInputValue(row.starts_at);
    setEditingId(row.id);
    setStartDate(nextStartDate);
    setEndDate(toInclusiveEndDateValue(row.ends_at));
    setNotes(row.notes || '');
    setPickStep('start');
    if (nextStartDate) {
      const parsed = dayjs(`${nextStartDate}T00:00:00`);
      if (parsed.isValid()) {
        setCalendarValue(parsed);
      }
    }
    setFeedback(null);
  };

  const handleCalendarSelect = (value: Dayjs, info?: { source?: string }) => {
    if (info?.source && info.source !== 'date') {
      setCalendarValue(value);
      return;
    }

    const selectedDate = value.format('YYYY-MM-DD');
    setFeedback(null);
    setCalendarValue(value);

    if (pickStep === 'start' || !startDate) {
      setStartDate(selectedDate);
      if (endDate && selectedDate > endDate) {
        setEndDate('');
      }
      setPickStep('end');
      return;
    }

    const startDay = dayjs(`${startDate}T00:00:00`);
    if (!startDay.isValid() || value.isBefore(startDay, 'day')) {
      setStartDate(selectedDate);
      setEndDate('');
      setPickStep('end');
      return;
    }

    setEndDate(selectedDate);
    setPickStep('start');
  };

  const handleSave = async () => {
    if (!selectedUserId) {
      setFeedback('Choose a member first.');
      return;
    }
    const range = toStartEndIso(startDate, endDate);
    if (!range) {
      setFeedback('Choose a valid start/end range.');
      return;
    }

    try {
      setFeedback(null);
      if (editingId) {
        await planner.updateMemberUnavailability({
          unavailabilityId: editingId,
          updates: {
            starts_at: range.startsAt,
            ends_at: range.endsAt,
            notes: notes || null,
            updated_by: user?.id || null,
          },
        });
        setFeedback('Unavailability updated.');
      } else {
        await planner.createMemberUnavailability({
          user_id: selectedUserId,
          starts_at: range.startsAt,
          ends_at: range.endsAt,
          all_day: true,
          title: 'Unavailable',
          notes: notes || null,
          created_by: user?.id || null,
          updated_by: user?.id || null,
        });
        setFeedback('Unavailability added.');
      }
      resetForm();
    } catch (error) {
      setFeedback(error instanceof Error ? error.message : 'Unable to save unavailability.');
    }
  };

  const handleDelete = async (rowId: string) => {
    try {
      setFeedback(null);
      await planner.deleteMemberUnavailability(rowId);
      if (editingId === rowId) {
        resetForm();
      }
      setFeedback('Unavailability removed.');
    } catch (error) {
      setFeedback(error instanceof Error ? error.message : 'Unable to remove unavailability.');
    }
  };

  if (planner.isLoading) {
    return (
      <PlannerPage>
        <PlannerCard>
          <PlannerHeading>Calendar</PlannerHeading>
          <PlannerSubtle>Loading availability calendar...</PlannerSubtle>
        </PlannerCard>
      </PlannerPage>
    );
  }

  if (planner.isError) {
    return (
      <PlannerPage>
        <PlannerCard>
          <PlannerHeading>Calendar</PlannerHeading>
          <PlannerError>Unable to load calendar availability.</PlannerError>
        </PlannerCard>
      </PlannerPage>
    );
  }

  return (
    <PlannerPage>
      <Grid>
        <PlannerCard>
          <PlannerKicker>Availability Calendar</PlannerKicker>
          <PlannerHeading>{editingId ? 'Edit Unavailability' : 'Add Unavailability'}</PlannerHeading>
          <PlannerSubtle>
            Mark date ranges where a member cannot be scheduled.
          </PlannerSubtle>

          {canManageOthers ? (
            <Field style={{ marginTop: '0.7rem' }}>
              Member
              <Select value={selectedUserId} onChange={(event) => setSelectedUserId(event.target.value)}>
                {peopleOptions.map((person) => (
                  <option key={person.id} value={person.id}>
                    {getPersonDisplayLabel(person)}
                  </option>
                ))}
              </Select>
            </Field>
          ) : null}

          <PlannerSubtle style={{ marginTop: '0.7rem' }}>
            Click the calendar to pick dates. Step: {pickStep === 'start' ? 'Choose Start Date' : 'Choose End Date'}.
          </PlannerSubtle>
          <CalendarFrame>
            <Calendar
              fullscreen={false}
              value={calendarValue}
              onSelect={handleCalendarSelect}
              onPanelChange={(value) => setCalendarValue(value)}
            />
          </CalendarFrame>
          <Field style={{ marginTop: '0.7rem' }}>
            Start Date
            <Input
              type="date"
              value={startDate}
              onChange={(event) => {
                const nextStartDate = event.target.value;
                setStartDate(nextStartDate);
                if (endDate && nextStartDate > endDate) {
                  setEndDate('');
                }
              }}
            />
          </Field>
          <Field style={{ marginTop: '0.7rem' }}>
            End Date
            <Input type="date" value={endDate} min={startDate || undefined} onChange={(event) => setEndDate(event.target.value)} />
          </Field>
          <Field style={{ marginTop: '0.7rem' }}>
            Notes
            <Textarea
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              placeholder="Optional details for planners"
            />
          </Field>

          <ActionRow>
            <Button
              type="button"
              $tone="muted"
              onClick={() => setPickStep('start')}
            >
              Pick Start
            </Button>
            <Button
              type="button"
              $tone="muted"
              onClick={() => setPickStep('end')}
              disabled={!startDate}
            >
              Pick End
            </Button>
            <Button type="button" onClick={() => void handleSave()}>
              {editingId ? 'Update Range' : 'Add Range'}
            </Button>
            {editingId ? (
              <Button type="button" $tone="muted" onClick={resetForm}>
                Cancel Edit
              </Button>
            ) : null}
          </ActionRow>
          {feedback ? <PlannerSubtle style={{ marginTop: '0.7rem' }}>{feedback}</PlannerSubtle> : null}
        </PlannerCard>

        <PlannerCard>
          <PlannerKicker>Upcoming Blocks</PlannerKicker>
          <PlannerHeading>Unavailable Dates</PlannerHeading>
          <PlannerSubtle>
            These ranges are used by scheduling and auto-assignment.
          </PlannerSubtle>
          {selectedRows.length === 0 ? (
            <PlannerSubtle style={{ marginTop: '0.75rem' }}>No blocked ranges saved yet.</PlannerSubtle>
          ) : (
            <RangeList style={{ marginTop: '0.75rem' }}>
              {selectedRows.map((row) => (
                <RangeCard key={row.id}>
                  <Row>
                    <PlannerSubtle style={{ margin: 0, color: plannerColors.text }}>
                      {formatRange(row.starts_at, row.ends_at)}
                    </PlannerSubtle>
                    <ActionRow style={{ marginTop: 0 }}>
                      <Button type="button" $tone="muted" onClick={() => handleEdit(row)}>
                        Edit
                      </Button>
                      <Button type="button" $tone="danger" onClick={() => void handleDelete(row.id)}>
                        Remove
                      </Button>
                    </ActionRow>
                  </Row>
                  {row.notes ? <PlannerSubtle style={{ marginTop: '0.45rem' }}>{row.notes}</PlannerSubtle> : null}
                </RangeCard>
              ))}
            </RangeList>
          )}
        </PlannerCard>
      </Grid>
    </PlannerPage>
  );
}
